import { cents, invoiceVariance, tryParseMoney } from '@sitzmark/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import {
  Badge,
  Button,
  Callout,
  Card,
  Loading,
  Modal,
  Mono,
  Screen,
  SectionNav,
} from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { sizeGrids } from './size-grid.js';
import './receiving-document.css';
import './receiving.css';
import { DateInput } from '../../components/date-input.js';

/**
 * Checking a vendor's invoice against the order and the delivery (SCOPE §6.3,
 * decision 128).
 *
 * The invoice arrives after the goods. Every line starts at what the order
 * agreed and what arrived, so a clean invoice is: type its number, press
 * Save. A wrong one is a few typed figures — and the screen says, before
 * saving, what they add up to. The preview uses the same `invoiceVariance`
 * the server saves with (the client may preview with the server's primitive,
 * never a second implementation).
 *
 * The result is a variance report to send back: what was agreed, what was
 * billed, and the total to credit.
 */

type Draft = Awaited<ReturnType<typeof api.purchasing.invoiceDraft.query>>;
type InvoiceView = Awaited<ReturnType<typeof api.purchasing.invoice.query>>;

const STATUS_WORDS: Record<string, string> = {
  draft: 'draft',
  matched: 'matches',
  variance: 'differs',
  approved: 'settled',
};

export function InvoiceScreen({
  purchaseOrderId,
  onDone,
}: {
  purchaseOrderId: string;
  onDone: () => void;
}) {
  const [viewing, setViewing] = useState<string | null>(null);

  const draft = useQuery({
    queryKey: ['purchasing', 'invoiceDraft', purchaseOrderId],
    queryFn: () => api.purchasing.invoiceDraft.query({ purchaseOrderId }),
    refetchOnMount: 'always',
  });

  if (viewing !== null) {
    return <InvoiceResult invoiceId={viewing} onBack={() => setViewing(null)} onDone={onDone} />;
  }
  if (draft.isPending) {
    return (
      <Screen title="Check an invoice" width="wide">
        <Loading label="Loading the order" />
      </Screen>
    );
  }
  if (draft.isError) {
    return (
      <Screen
        title="Check an invoice"
        width="wide"
        secondaryActions={
          <Button variant="ghost" onClick={onDone}>
            Back to purchase orders
          </Button>
        }
      >
        <Callout tone="danger">{errorMessage(draft.error)}</Callout>
      </Screen>
    );
  }
  return <InvoiceEntry draft={draft.data} onSaved={setViewing} onDone={onDone} />;
}

function InvoiceEntry({
  draft,
  onSaved,
  onDone,
}: {
  draft: Draft;
  onSaved: (invoiceId: string) => void;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [reference, setReference] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  // Smart default (§2.2-5): the invoice bills what arrived, at the agreed cost.
  const [lines, setLines] = useState(() =>
    draft.lines.map((line) => ({
      purchaseOrderLineId: line.purchaseOrderLineId,
      quantity: String(line.quantityReceived),
      cost: (line.orderedCostCents / 100).toFixed(2),
    })),
  );

  const parsed = lines.map((line, index) => {
    const quantity = /^\d+$/.test(line.quantity.trim()) ? Number(line.quantity.trim()) : null;
    const cost = tryParseMoney(line.cost);
    return { ...line, index, units: quantity, costCents: cost };
  });
  const unreadable = parsed.some((line) => line.units === null || line.costCents === null);
  const billed = parsed.filter((line) => (line.units ?? 0) > 0);

  const preview = unreadable
    ? null
    : invoiceVariance(
        billed.map((line) => {
          const order = draft.lines[line.index]!;
          return {
            receivedQuantity: order.quantityReceived,
            orderedCostCents: cents(order.orderedCostCents),
            invoicedQuantity: line.units!,
            invoicedCostCents: cents(line.costCents!),
          };
        }),
      );
  // Row → its variance, so a long order is not searched once per row.
  const varianceByIndex = new Map(
    preview === null ? [] : billed.map((line, at) => [line.index, preview.lines[at]!] as const),
  );

  const save = useMutation({
    mutationFn: () =>
      api.purchasing.saveInvoice.mutate({
        purchaseOrderId: draft.order.id,
        vendorInvoiceNumber: reference.trim(),
        invoiceDate: invoiceDate === '' ? null : invoiceDate,
        lines: parsed.map((line) => ({
          purchaseOrderLineId: line.purchaseOrderLineId,
          quantity: line.units!,
          invoicedCostCents: line.costCents!,
        })),
      }),
    onSuccess: (saved) => {
      recordBreadcrumb('purchasing', `entered invoice ${saved.number} on ${draft.order.number}`);
      void queryClient.invalidateQueries({ queryKey: ['purchasing'] });
      onSaved(saved.id);
    },
  });

  const blocked =
    reference.trim() === ''
      ? 'Type the invoice number from the vendor’s paper'
      : unreadable
        ? 'One of those is not a whole quantity or an amount'
        : billed.length === 0
          ? 'The invoice bills nothing — enter at least one line'
          : undefined;

  const set = (index: number, patch: Partial<{ quantity: string; cost: string }>) =>
    setLines((current) => current.map((line, at) => (at === index ? { ...line, ...patch } : line)));

  /*
   * Grid or list (2026-10-02): "should check an invoice have the option to be
   * a grid layout and not just a list?" The grid is each style as it was
   * ordered — billed units in each box, the style's billed price once in its
   * heading, as most vendors bill. List keeps a price per size.
   */
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const styles = invoiceStyles(draft.lines);
  /** One price for every size of a style — the box shows it only while they agree. */
  const setStyleCost = (indexes: readonly number[], cost: string) =>
    setLines((current) =>
      current.map((line, at) => (indexes.includes(at) ? { ...line, cost } : line)),
    );

  return (
    <Screen
      title={
        <>
          Check an invoice · <Mono>{draft.order.number}</Mono>
        </>
      }
      subtitle={`From ${draft.order.vendorName}. Every line starts at what the order agreed and what arrived — change only what the invoice says differently.`}
      width="wide"
      primaryAction={
        <Button
          variant="primary"
          busy={save.isPending}
          disabledReason={blocked}
          onClick={() => save.mutate()}
        >
          Save invoice
        </Button>
      }
      secondaryActions={
        <Button variant="ghost" onClick={onDone}>
          Back to purchase orders
        </Button>
      }
    >
      {save.isError ? <Callout tone="danger">{errorMessage(save.error)}</Callout> : null}

      {draft.invoices.length > 0 ? (
        <Card title="Already entered for this order">
          <ul className="invoice__list">
            {draft.invoices.map((invoice) => (
              <li key={invoice.id}>
                <button type="button" className="link-button" onClick={() => onSaved(invoice.id)}>
                  <Mono>{invoice.number}</Mono> · their {invoice.vendorInvoiceNumber}
                </button>{' '}
                <Badge tone={invoice.status === 'variance' ? 'warning' : 'neutral'}>
                  {STATUS_WORDS[invoice.status] ?? invoice.status}
                </Badge>
                {invoice.varianceCents > 0
                  ? ` · ${money(invoice.varianceCents)} ${invoice.status === 'approved' ? 'over-billed' : 'to ask back'}`
                  : ''}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <div className="invoice__head">
        <div className="field">
          <label className="field__label" htmlFor="invoice-reference">
            Their invoice number
          </label>
          <input
            id="invoice-reference"
            className="input"
            autoFocus
            maxLength={60}
            value={reference}
            onChange={(event) => setReference(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="invoice-date">
            Invoice date
          </label>
          <DateInput
            id="invoice-date"

            value={invoiceDate}
            onChange={(value) => setInvoiceDate(value)}
          />
        </div>
      </div>

      <SectionNav
        label="Invoice layout"
        current={view}
        items={[
          { key: 'grid', label: 'Grid' },
          { key: 'list', label: 'List' },
        ]}
        onChange={setView}
      />

      {view === 'grid' ? (
        <Card padded={false}>
          {styles.map((style) => {
            const indexes = style.lines.map((entry) => entry.index);
            const costs = new Set(indexes.map((index) => lines[index]!.cost));
            const agreed = new Set(style.lines.map((entry) => entry.order.orderedCostCents));
            const price = indexes.reduce(
              (sum, index) => sum + (varianceByIndex.get(index)?.priceVarianceCents ?? 0),
              0,
            );
            const quantity = indexes.reduce(
              (sum, index) => sum + (varianceByIndex.get(index)?.quantityVarianceCents ?? 0),
              0,
            );
            return (
              <section
                key={style.productId}
                className="invoice__style"
                aria-label={`${style.productName} on this invoice`}
              >
                <div className="invoice__style-head">
                  <strong>{style.productName}</strong>
                  <span className="text-secondary">
                    Agreed {agreed.size === 1 ? money([...agreed][0]!) : 'by size — see List'}
                  </span>
                  <label className="invoice__style-cost">
                    <span className="text-secondary">Billed each</span>
                    <input
                      className="input invoice__cell money"
                      inputMode="decimal"
                      aria-label={`Billed each, every size of ${style.productName}`}
                      placeholder="Varies"
                      value={costs.size === 1 ? [...costs][0]! : ''}
                      onChange={(event) =>
                        setStyleCost(indexes, event.target.value.replace(/[^\d.]/g, ''))
                      }
                    />
                  </label>
                  <DifferenceWords
                    price={price}
                    quantity={quantity}
                    billedQuantity={indexes.reduce(
                      (sum, index) => sum + (parsed[index]!.units ?? 0),
                      0,
                    )}
                    received={style.lines.reduce(
                      (sum, entry) => sum + entry.order.quantityReceived,
                      0,
                    )}
                  />
                </div>
                {sizeGrids(style.lines).map((grid) => (
                  <div className="table-scroll" key={grid.altSize ?? 'one'}>
                    <table className="table matrix">
                      {grid.label !== null ? (
                        <caption className="matrix__caption">{grid.label}</caption>
                      ) : null}
                      <thead>
                        <tr>
                          <th />
                          {grid.sizes.map((size, at) => (
                            <th key={size ?? `size-${at}`} className="num">
                              {size ?? 'One size'}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {grid.rows.map((row, rowAt) => (
                          <tr key={row.key}>
                            <th scope="row" className="matrix__row-head">
                              {row.label}
                            </th>
                            {row.cells.map((entry, at) =>
                              entry === undefined ? (
                                <td key={at} />
                              ) : (
                                <td key={entry.order.purchaseOrderLineId} className="matrix__cell">
                                  <input
                                    className="input invoice__cell"
                                    inputMode="numeric"
                                    aria-label={`Billed units, ${entry.order.description} (${entry.order.sku})`}
                                    value={lines[entry.index]!.quantity}
                                    onChange={(event) =>
                                      set(entry.index, {
                                        quantity: event.target.value.replace(/[^\d]/g, ''),
                                      })
                                    }
                                  />
                                  <span className="invoice__cell-note">
                                    of {entry.order.quantityReceived} received
                                  </span>
                                  <span className="invoice__cell-note">
                                    <Mono>{entry.order.sku}</Mono>
                                  </span>
                                </td>
                              ),
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </section>
            );
          })}
        </Card>
      ) : (
        <Card padded={false}>
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Ordered</th>
                  <th className="num">Received</th>
                  <th className="num">Agreed each</th>
                  <th className="num">Billed units</th>
                  <th className="num">Billed each</th>
                  <th className="num">Difference</th>
                </tr>
              </thead>
              <tbody>
                {parsed.map((line) => {
                  const order = draft.lines[line.index]!;
                  const variance = varianceByIndex.get(line.index) ?? null;
                  const label = `${order.description} (${order.sku})`;
                  return (
                    <tr key={line.purchaseOrderLineId}>
                      <td>
                        {order.description} <span className="text-secondary">{order.sku}</span>
                      </td>
                      <td className="num">{order.quantityOrdered}</td>
                      <td className="num">{order.quantityReceived}</td>
                      <td className="num money">{money(order.orderedCostCents)}</td>
                      <td className="num">
                        <input
                          className="input invoice__cell"
                          inputMode="numeric"
                          aria-label={`Billed units, ${label}`}
                          value={line.quantity}
                          onChange={(event) =>
                            set(line.index, { quantity: event.target.value.replace(/[^\d]/g, '') })
                          }
                        />
                      </td>
                      <td className="num">
                        <input
                          className="input invoice__cell money"
                          inputMode="decimal"
                          aria-label={`Billed each, ${label}`}
                          value={line.cost}
                          onChange={(event) =>
                            set(line.index, { cost: event.target.value.replace(/[^\d.]/g, '') })
                          }
                        />
                      </td>
                      <td className="num">
                        <DifferenceWords
                          price={variance?.priceVarianceCents ?? 0}
                          quantity={variance?.quantityVarianceCents ?? 0}
                          billedQuantity={line.units ?? 0}
                          received={order.quantityReceived}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {preview !== null ? (
        <p className="invoice__summary" role="status">
          {preview.matched
            ? `Matches the order and the delivery — ${money(preview.billedCents)} billed.`
            : `${money(preview.billedCents)} billed. ${money(preview.overBilledCents)} to ask back${
                preview.underBilledCents > 0
                  ? `; ${money(preview.underBilledCents)} billed under what was agreed or received`
                  : ''
              }.`}
        </p>
      ) : null}
    </Screen>
  );
}

/** A difference said in words: never a bare signed number (DESIGN-DNA Part 3). */
/** The invoice's lines, style by style, each knowing its place in the draft. */
function invoiceStyles(lines: Draft['lines']) {
  const styles = new Map<
    string,
    {
      productId: string;
      productName: string;
      lines: {
        index: number;
        order: Draft['lines'][number];
        colorName: string | null;
        sizeLabel: string | null;
        altSize: string | null;
        sizeSort: number;
      }[];
    }
  >();
  lines.forEach((order, index) => {
    const style = styles.get(order.productId) ?? {
      productId: order.productId,
      productName: order.productName,
      lines: [],
    };
    style.lines.push({
      index,
      order,
      colorName: order.colorName,
      sizeLabel: order.sizeLabel,
      altSize: order.altSize,
      sizeSort: order.sizeSort,
    });
    styles.set(order.productId, style);
  });
  return [...styles.values()];
}

function DifferenceWords({
  price,
  quantity,
  billedQuantity,
  received,
}: {
  price: number;
  quantity: number;
  billedQuantity: number;
  received: number;
}) {
  const parts: string[] = [];
  // The line's total, with the working when there is more than one unit —
  // "$10.00 over" alone reads as ten dollars a unit.
  const working =
    billedQuantity > 1 ? ` (${billedQuantity} × ${money(Math.abs(price) / billedQuantity)})` : '';
  if (price > 0) parts.push(`${money(price)} over the agreed cost${working}`);
  if (price < 0) parts.push(`${money(-price)} under the agreed cost${working}`);
  if (quantity > 0) {
    parts.push(`${billedQuantity - received} billed not received (${money(quantity)})`);
  }
  if (quantity < 0) {
    parts.push(`${received - billedQuantity} received not billed (${money(-quantity)})`);
  }
  if (parts.length === 0) return <span className="text-secondary">Matches</span>;
  return <span className="invoice__differs">{parts.join(' · ')}</span>;
}

function InvoiceResult({
  invoiceId,
  onBack,
  onDone,
}: {
  invoiceId: string;
  onBack: () => void;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const key = ['purchasing', 'invoice', invoiceId];
  const invoice = useQuery({
    queryKey: key,
    queryFn: () => api.purchasing.invoice.query({ invoiceId }),
    refetchOnMount: 'always',
  });
  const report = useVarianceReport();
  const [confirming, setConfirming] = useState(false);

  const settle = useMutation({
    mutationFn: () => api.purchasing.approveInvoice.mutate({ invoiceId }),
    onSuccess: (next) => {
      recordBreadcrumb('purchasing', `settled invoice ${next.invoice.number}`);
      queryClient.setQueryData(key, next);
      void queryClient.invalidateQueries({ queryKey: ['purchasing', 'invoiceDraft'] });
    },
  });

  if (invoice.isPending) {
    return (
      <Screen title="Invoice" width="wide">
        <Loading label="Loading the invoice" />
      </Screen>
    );
  }
  if (invoice.isError) {
    return (
      <Screen
        title="Invoice"
        width="wide"
        secondaryActions={
          <Button variant="ghost" onClick={onBack}>
            Back
          </Button>
        }
      >
        <Callout tone="danger">{errorMessage(invoice.error)}</Callout>
      </Screen>
    );
  }

  const data = invoice.data;
  const status = data.invoice.status;
  const differing = data.lines.filter(
    (line) => line.priceVarianceCents !== 0 || line.quantityVarianceCents !== 0,
  );

  return (
    <Screen
      title={
        <>
          <Mono>{data.invoice.number}</Mono> · their {data.invoice.vendorInvoiceNumber}
        </>
      }
      subtitle={`${data.invoice.vendor.name} · against ${data.invoice.purchaseOrderNumber ?? 'no order'}`}
      width="wide"
      primaryAction={
        status === 'approved' ? undefined : (
          // Final, so one clear confirmation (DESIGN-DNA, error prevention).
          <Button variant="primary" onClick={() => setConfirming(true)}>
            {data.matched ? 'Approve for payment' : 'Mark settled'}
          </Button>
        )
      }
      secondaryActions={
        <>
          <Button variant="ghost" onClick={onDone}>
            Back to purchase orders
          </Button>
          <Button variant="ghost" onClick={onBack}>
            Check another invoice
          </Button>
          {data.matched ? null : (
            <Button variant="secondary" onClick={() => report.print(data)}>
              Print variance report
            </Button>
          )}
        </>
      }
    >
      {report.frame}
      {settle.isError ? <Callout tone="danger">{errorMessage(settle.error)}</Callout> : null}

      {status === 'approved' ? (
        // Settled: what happened, not what to do — the asking is over.
        <Callout
          tone="success"
          title={`Settled with ${data.invoice.vendor.name} on ${new Date(data.invoice.updatedAt).toLocaleDateString()}`}
        >
          {data.matched
            ? `It matched the order and the delivery — ${money(data.billedCents)}.`
            : `${differing.length} line${differing.length === 1 ? '' : 's'} differed; ${money(data.overBilledCents)} was over-billed. The report still prints for the file.`}
        </Callout>
      ) : data.matched ? (
        <Callout tone="success" title="The invoice matches">
          Every line bills what arrived at the cost the order agreed — {money(data.billedCents)}.
        </Callout>
      ) : data.overBilledCents > 0 ? (
        <Callout tone="warning" title={`${money(data.overBilledCents)} to ask back`}>
          {differing.length} line{differing.length === 1 ? '' : 's'} differ from the order or the
          delivery. Print the variance report and send it to {data.invoice.vendor.name}.
          {data.underBilledCents > 0
            ? ` They also billed ${money(data.underBilledCents)} under what was agreed or received — shown on the report, and not netted off.`
            : ''}
        </Callout>
      ) : (
        <Callout tone="warning" title="Billed under the order">
          {data.invoice.vendor.name} billed {money(data.underBilledCents)} less than what was agreed
          or received. There is nothing to ask back — check whether a second invoice for the rest is
          still to come.
        </Callout>
      )}

      <Modal
        open={confirming}
        title={data.matched ? 'Approve this invoice for payment?' : 'Mark this invoice settled?'}
        description={
          data.matched
            ? 'It matches the order and the delivery.'
            : `Only once ${data.invoice.vendor.name} has sent a credit, or the shop has decided to accept the difference.`
        }
        onClose={() => setConfirming(false)}
        primaryAction={
          <Button
            variant="primary"
            busy={settle.isPending}
            onClick={() => settle.mutate(undefined, { onSettled: () => setConfirming(false) })}
          >
            {data.matched ? 'Approve for payment' : 'Mark settled'}
          </Button>
        }
        secondaryAction={
          <Button variant="ghost" onClick={() => setConfirming(false)}>
            Not yet
          </Button>
        }
      >
        <p>This is final: a settled invoice is not changed again.</p>
      </Modal>

      <Card padded={false}>
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Item</th>
                <th className="num">Received</th>
                <th className="num">Billed units</th>
                <th className="num">Agreed each</th>
                <th className="num">Billed each</th>
                <th className="num">Difference</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((line) => (
                <tr key={line.purchaseOrderLineId}>
                  <td>
                    {line.description} <span className="text-secondary">{line.sku}</span>
                  </td>
                  <td className="num">{line.quantityReceived}</td>
                  <td className="num">{line.invoicedQuantity}</td>
                  <td className="num money">{money(line.orderedCostCents)}</td>
                  <td className="num money">{money(line.invoicedCostCents)}</td>
                  <td className="num">
                    <DifferenceWords
                      price={line.priceVarianceCents}
                      quantity={line.quantityVarianceCents}
                      billedQuantity={line.invoicedQuantity}
                      received={line.quantityReceived}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Screen>
  );
}

const PAGE_RULE = '@page { size: letter; margin: 0.6in; }';

/** The paper sent back to the vendor: what was agreed, what was billed, what to credit. */
function useVarianceReport() {
  const [printing, setPrinting] = useState<InvoiceView | null>(null);
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.settings.get.query(),
    staleTime: 5 * 60_000,
  });
  const store = settings.data?.store;

  const print = useCallback((data: InvoiceView) => {
    // Letter paper, like the order and the receiving document it is read against.
    const style = window.document.createElement('style');
    style.textContent = PAGE_RULE;
    window.document.head.appendChild(style);
    try {
      // Synchronously, before `window.print()` — React would otherwise batch
      // the render until after the dialog had captured a blank page.
      flushSync(() => setPrinting(data));
      window.print();
    } finally {
      setPrinting(null);
      style.remove();
    }
  }, []);

  const frame =
    printing === null
      ? null
      : createPortal(
          <div className="print-only">
            <article className="rodoc">
              <header className="rodoc__head">
                <div>
                  <div className="rodoc__store">{store?.name ?? 'Sitzmark'}</div>
                  {store?.addressLine1 ? <div>{store.addressLine1}</div> : null}
                  {store?.city ? (
                    <div>
                      {store.city}
                      {store.region ? `, ${store.region}` : ''} {store.postalCode ?? ''}
                    </div>
                  ) : null}
                  {store?.phone ? <div>{store.phone}</div> : null}
                </div>
                <div className="rodoc__title">
                  <h1>Invoice discrepancy</h1>
                  <div className="rodoc__number">{printing.invoice.vendorInvoiceNumber}</div>
                </div>
              </header>
              <dl className="rodoc__meta">
                <div>
                  <dt>Vendor</dt>
                  <dd>
                    {printing.invoice.vendor.name}
                    {printing.invoice.vendor.addressLine1 ? (
                      <>
                        <br />
                        {printing.invoice.vendor.addressLine1}
                      </>
                    ) : null}
                    {printing.invoice.vendor.city ? (
                      <>
                        <br />
                        {printing.invoice.vendor.city}
                        {printing.invoice.vendor.region
                          ? `, ${printing.invoice.vendor.region}`
                          : ''}{' '}
                        {printing.invoice.vendor.postalCode ?? ''}
                      </>
                    ) : null}
                  </dd>
                </div>
                <div>
                  <dt>Our order</dt>
                  <dd>{printing.invoice.purchaseOrderNumber ?? '—'}</dd>
                </div>
                <div>
                  <dt>Our reference</dt>
                  <dd>{printing.invoice.number}</dd>
                </div>
                <div>
                  <dt>Your invoice</dt>
                  <dd>
                    {printing.invoice.vendorInvoiceNumber}
                    {printing.invoice.invoiceDate
                      ? ` of ${new Date(`${printing.invoice.invoiceDate}T12:00:00`).toLocaleDateString()}`
                      : ''}
                  </dd>
                </div>
              </dl>
              <table className="rodoc__lines">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">Received</th>
                    <th className="num">Billed</th>
                    <th className="num">Agreed each</th>
                    <th className="num">Billed each</th>
                    <th className="num">Over-billed</th>
                  </tr>
                </thead>
                <tbody>
                  {printing.lines
                    .filter(
                      (line) => line.priceVarianceCents !== 0 || line.quantityVarianceCents !== 0,
                    )
                    .map((line) => (
                      <tr key={line.purchaseOrderLineId}>
                        <td>
                          {line.description} <span className="code">{line.sku}</span>
                        </td>
                        <td className="num">{line.quantityReceived}</td>
                        <td className="num">{line.invoicedQuantity}</td>
                        <td className="num">{money(line.orderedCostCents)}</td>
                        <td className="num">{money(line.invoicedCostCents)}</td>
                        <td className="num">
                          {money(
                            Math.max(line.priceVarianceCents, 0) +
                              Math.max(line.quantityVarianceCents, 0),
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={5}>Please credit</th>
                    <th className="num">{money(printing.overBilledCents)}</th>
                  </tr>
                </tfoot>
              </table>
              {printing.underBilledCents > 0 ? (
                <p className="rodoc__notes">
                  The invoice also bills {money(printing.underBilledCents)} under what was agreed or
                  received. It is shown for your records and is not netted against the credit.
                </p>
              ) : null}
            </article>
          </div>,
          window.document.body,
        );

  return { print, frame };
}
